import { useState, useEffect } from "react";
import { useAuth } from "@/lib/store/auth-store";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { clearAllUserData } from "@/lib/utils/local-storage-cleanup";
import { AlertTriangle, Trash2, Loader2, Eye, EyeOff } from "lucide-react";

interface UserDataSummary {
  deckCount: number;
  isVerified: boolean;
}

export function AccountSettings() {
  const { user, logout } = useAuth();
  const [dataSummary, setDataSummary] = useState<UserDataSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [emailRevealed, setEmailRevealed] = useState(false);
  const [discordIdRevealed, setDiscordIdRevealed] = useState(false);

  const CONFIRM_TEXT = "DELETE";

  const obfuscateEmail = (email: string): string => {
    const [localPart, domain] = email.split("@");
    if (!domain || !localPart) return email;
    
    const obfuscatedLocal = localPart.length > 1 
      ? `${localPart[0]}${"*".repeat(Math.min(localPart.length - 1, 3))}`
      : localPart;
    
    const [domainName, ...tldParts] = domain.split(".");
    const tld = tldParts.join(".");
    if (!domainName) return email;
    
    const obfuscatedDomain = domainName.length > 1
      ? `${domainName[0]}${"*".repeat(Math.min(domainName.length - 1, 3))}`
      : domainName;
    
    return `${obfuscatedLocal}@${obfuscatedDomain}${tld ? `.${tld}` : ""}`;
  };

  const obfuscateDiscordId = (discordId: string): string => {
    if (!discordId || discordId.length <= 4) return discordId;
    const visibleChars = 2;
    const hiddenChars = discordId.length - visibleChars;
    return `${discordId.slice(0, visibleChars)}${"*".repeat(Math.min(hiddenChars, 6))}`;
  };

  useEffect(() => {
    if (user) {
      fetchDataSummary();
    }
  }, [user]);

  const fetchDataSummary = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/user/data-summary", {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setDataSummary(data);
      } else {
        setError("Failed to load account information");
      }
    } catch (err) {
      console.error("Error fetching data summary:", err);
      setError("Failed to load account information");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setConfirmText("");
    setError(null);
  };

  const handleDeleteConfirm = async () => {
    if (confirmText !== CONFIRM_TEXT) {
      setError(`Please type "${CONFIRM_TEXT}" to confirm`);
      return;
    }

    try {
      setDeleting(true);
      setError(null);

      const res = await fetch("/api/user/delete-data", {
        method: "POST",
        credentials: "include",
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to delete data");
      }

      // Clear local storage
      clearAllUserData();

      // Show success message briefly
      setSuccess(true);
      setDeleteDialogOpen(false);

      // Logout and redirect after a short delay
      setTimeout(async () => {
        await logout();
      }, 1500);
    } catch (err) {
      console.error("Error deleting user data:", err);
      setError(
        err instanceof Error ? err.message : "Failed to delete data. Please try again."
      );
    } finally {
      setDeleting(false);
    }
  };

  if (!user) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl font-bold mb-6">Account Settings</h1>
            <p className="text-muted-foreground">
              You must be logged in to view your account settings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold mb-6">Account Settings</h1>

          <div className="space-y-8">
            {/* User Information Section */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">Account Information</h2>
              <div className="rounded-lg border p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Email
                  </label>
                  <div className="flex items-center gap-2">
                    <p className="text-foreground flex-1">
                      {emailRevealed ? user.email : obfuscateEmail(user.email || "")}
                    </p>
                    <button
                      onClick={() => setEmailRevealed(!emailRevealed)}
                      className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1"
                      aria-label={emailRevealed ? "Hide email" : "Reveal email"}
                      type="button"
                    >
                      {emailRevealed ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Name
                  </label>
                  <p className="text-foreground">{user.name}</p>
                </div>
                {user.discordId && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Discord ID
                    </label>
                    <div className="flex items-center gap-2">
                      <p className="text-foreground font-mono text-sm flex-1">
                        {discordIdRevealed ? user.discordId : obfuscateDiscordId(user.discordId)}
                      </p>
                      <button
                        onClick={() => setDiscordIdRevealed(!discordIdRevealed)}
                        className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1"
                        aria-label={discordIdRevealed ? "Hide Discord ID" : "Reveal Discord ID"}
                        type="button"
                      >
                        {discordIdRevealed ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                {user.isAdmin && (
                  <div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary text-primary-foreground">
                      Admin
                    </span>
                  </div>
                )}
                {user.isVerified && (
                  <div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500 text-white">
                      Verified
                    </span>
                  </div>
                )}
              </div>
            </section>

            {/* Data Summary Section */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">Your Data</h2>
              <div className="rounded-lg border p-6 space-y-4">
                {loading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading...</span>
                  </div>
                ) : error ? (
                  <p className="text-destructive">{error}</p>
                ) : dataSummary ? (
                  <>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Decks Created
                      </label>
                      <p className="text-foreground">{dataSummary.deckCount}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Verified Status
                      </label>
                      <p className="text-foreground">
                        {dataSummary.isVerified ? "Yes" : "No"}
                      </p>
                    </div>
                  </>
                ) : null}
              </div>
            </section>

            {/* Delete Data Section */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-destructive">
                Delete All My Data
              </h2>
              <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                  <div className="space-y-2">
                    <p className="text-foreground">
                      This action will permanently delete all your data from our
                      website. This includes:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
                      <li>All decks you have created ({dataSummary?.deckCount || 0} decks)</li>
                      <li>Your verified status</li>
                      <li>All preferences stored in your browser</li>
                    </ul>
                    <p className="text-foreground font-medium mt-4">
                      This action cannot be undone. You will be logged out
                      immediately after deletion.
                    </p>
                    {user.isAdmin && (
                      <p className="text-destructive font-medium mt-2">
                        Note: Admin users cannot delete their data through this
                        interface. Please contact support if you need assistance.
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="destructive"
                  onClick={handleDeleteClick}
                  disabled={user.isAdmin || deleting || success}
                  className="w-full sm:w-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All My Data
                </Button>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent showCloseButton={!deleting}>
          <DialogHeader>
            <DialogTitle className="text-destructive">
              Confirm Data Deletion
            </DialogTitle>
            <DialogDescription>
              This action is permanent and cannot be undone. All your data will
              be deleted immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {error && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/50 p-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
            {success && (
              <div className="rounded-lg bg-green-500/10 border border-green-500/50 p-3">
                <p className="text-sm text-green-600 dark:text-green-400">
                  Your data has been deleted successfully. You will be logged out
                  shortly.
                </p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">
                Type <span className="font-mono font-bold">{CONFIRM_TEXT}</span>{" "}
                to confirm:
              </label>
              <Input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_TEXT}
                disabled={deleting || success}
                className="mt-2"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setConfirmText("");
                setError(null);
              }}
              disabled={deleting || success}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={confirmText !== CONFIRM_TEXT || deleting || success}
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All Data
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

